/**
 * Billing feature Redux root — assembles the per-entity billing slices
 * (accounts, agreements, notes, invoices, device/feature pricing and
 * categories, prepaid coverages) into the single `billing` reducer the store
 * mounts, and re-exports every billing selector and view-setter action.
 *
 * The per-entity pieces live in ./slices/*; this module wires their initial
 * state, reducers, and extra reducers into one createSlice so existing import
 * sites (`@sophocles/features/billing/slice`) keep resolving unchanged.
 */
import { createSlice } from '@reduxjs/toolkit';

import {
  accountAgreementsInitialState,
  addAccountAgreementCases,
} from '@sophocles/features/billing/slices/accountAgreements';
import { accountNotesInitialState, addAccountNoteCases } from '@sophocles/features/billing/slices/accountNotes';
import {
  addBillingAccountCases,
  billingAccountsInitialState,
  billingAccountsReducers,
} from '@sophocles/features/billing/slices/billingAccounts';
import { deviceCategoriesInitialState } from '@sophocles/features/billing/slices/deviceCategories';
import { addDevicePricingCases, devicePricingInitialState } from '@sophocles/features/billing/slices/devicePricing';
import { addFeaturePricingCases, featurePricingInitialState } from '@sophocles/features/billing/slices/featurePricing';
import { addInvoiceCases, invoicesInitialState, invoicesReducers } from '@sophocles/features/billing/slices/invoices';
import {
  prepaidCoveragesInitialState,
  prepaidCoveragesReducers,
} from '@sophocles/features/billing/slices/prepaidCoverages';

const slice = createSlice({
  name: 'billing',
  initialState: {
    accounts: billingAccountsInitialState,
    accountAgreements: accountAgreementsInitialState,
    accountNotes: accountNotesInitialState,
    deviceCategories: deviceCategoriesInitialState,
    devicePricing: devicePricingInitialState,
    featurePricing: featurePricingInitialState,
    invoices: invoicesInitialState,
    prepaidCoverages: prepaidCoveragesInitialState,
  },
  reducers: {
    ...billingAccountsReducers,
    ...invoicesReducers,
    ...prepaidCoveragesReducers,
  },
  extraReducers: builder => {
    addInvoiceCases(builder);
    addBillingAccountCases(builder);
    addAccountAgreementCases(builder);
    addAccountNoteCases(builder);
    addDevicePricingCases(builder);
    addFeaturePricingCases(builder);
  },
});

export {
  selectAccountInvoicesForPeriod,
  selectInvoice,
  selectInvoiceById,
  selectInvoiceEntities,
  selectInvoices,
  selectInvoiceViewProperties,
} from '@sophocles/features/billing/slices/invoices';
export {
  selectBillingAccount,
  selectBillingAccountById,
  selectBillingAccountEntities,
} from '@sophocles/features/billing/slices/billingAccounts';
export {
  selectBillingAccountAgreement,
  selectBillingAccountAgreementById,
  selectBillingAccountAgreementEntities,
  selectBillingAccountAgreementIds,
  selectBillingAccountAgreements,
  selectBillingAgreementsForAccount,
} from '@sophocles/features/billing/slices/accountAgreements';
export {
  selectBillingAccountNoteEntities,
  selectBillingNoteById,
  selectBillingNoteIds,
  selectBillingNotesForAccount,
} from '@sophocles/features/billing/slices/accountNotes';
export {
  selectDevicePricing,
  selectDevicePricingById,
  selectDevicePricingEntities,
  selectDevicePricingViewProperties,
} from '@sophocles/features/billing/slices/devicePricing';
export {
  selectFeaturePricing,
  selectFeaturePricingById,
  selectFeaturePricingEntities,
  selectFeaturePricingViewProperties,
} from '@sophocles/features/billing/slices/featurePricing';
export {
  selectAllDeviceBillingCategories,
  selectDeviceBillingCategory,
  selectDeviceBillingCategoryAutocompleteOptions,
  selectDeviceBillingCategoryEntities,
  selectDeviceBillingCategoryViewProperties,
} from '@sophocles/features/billing/slices/deviceCategories';
export {
  selectAllPrepaidCoverages,
  selectPrepaidCoverage,
  selectPrepaidCoverageById,
  selectPrepaidCoverageEntities,
  selectPrepaidCoverages,
  selectPrepaidCoveragesById,
  selectPrepaidCoverageViewProperties,
} from '@sophocles/features/billing/slices/prepaidCoverages';

export const {
  setInvoicePage,
  setInvoiceSearch,
  setInvoiceOrder,
  setInvoiceOrderBy,
  setBillingAccountPage,
  setBillingAccountPageSize,
  setBillingAccountFrequency,
  setBillingAccountCustomerType,
  setBillingAccountBillingStatus,
  setBillingAccountOrder,
  setBillingAccountOrderBy,
  setPrepaidCoveragePage,
  setPrepaidCoverageSearch,
  setPrepaidCoverageOrder,
  setPrepaidCoverageOrderBy,
} = slice.actions;

const { reducer } = slice;
export default reducer;
